/**
 * Calculates the delay date for sending notifications based on Jakarta timezone (UTC+7).
 *
 * Rules:
 * - If current time is between 10:00 AM - 3:00 PM Jakarta time today: returns current time
 * - Otherwise: returns 10:00 AM tomorrow
 *
 * @returns {Date} A Date object in UTC representing the target time in Jakarta timezone
 *
 * @example
 * // If current time is 12:00 PM Jakarta time
 * getJakartaDelay() // Returns current Date (12:00 PM)
 *
 * @example
 * // If current time is 5:00 PM Jakarta time
 * getJakartaDelay() // Returns Date for 10:00 AM tomorrow Jakarta time
 */
export const getJakartaDelay = (date: Date = new Date()): Date | undefined => {
  // Get current time in Jakarta timezone
  const jakartaFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Jakarta',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })

  const parts = jakartaFormatter.formatToParts(date)
  const jakartaDate = {
    year: parseInt(parts.find((p) => p.type === 'year')?.value ?? '0', 10),
    month: parseInt(parts.find((p) => p.type === 'month')?.value ?? '0', 10) - 1,
    day: parseInt(parts.find((p) => p.type === 'day')?.value ?? '0', 10),
    hour: parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0', 10),
    minute: parseInt(parts.find((p) => p.type === 'minute')?.value ?? '0', 10),
    second: parseInt(parts.find((p) => p.type === 'second')?.value ?? '0', 10),
  }

  // Check if between 10am (10) and 3pm (15) today
  if (jakartaDate.hour >= 10 && jakartaDate.hour < 15) {
    // Return current time
    return undefined
  } else {
    // Set to 10am tomorrow in Jakarta time
    // Create today at 10am Jakarta time, add 24 hours to get tomorrow
    const year = jakartaDate.year.toString().padStart(4, '0')
    const month = (jakartaDate.month + 1).toString().padStart(2, '0')
    const day = jakartaDate.day.toString().padStart(2, '0')
    const today10amJakarta = new Date(`${year}-${month}-${day}T10:00:00+07:00`)
    const tomorrow10amJakarta = new Date(today10amJakarta.getTime() + 24 * 60 * 60 * 1000)

    // Get tomorrow's date components in Jakarta timezone
    const tomorrowParts = jakartaFormatter.formatToParts(tomorrow10amJakarta)
    const tomorrowDate = {
      year: parseInt(tomorrowParts.find((p) => p.type === 'year')?.value ?? '0', 10),
      month: parseInt(tomorrowParts.find((p) => p.type === 'month')?.value ?? '0', 10),
      day: parseInt(tomorrowParts.find((p) => p.type === 'day')?.value ?? '0', 10),
    }

    // Create ISO string for tomorrow 10am with Jakarta timezone offset (+07:00)
    const tomorrowYear = tomorrowDate.year.toString().padStart(4, '0')
    const tomorrowMonth = tomorrowDate.month.toString().padStart(2, '0')
    const tomorrowDay = tomorrowDate.day.toString().padStart(2, '0')
    const isoString = `${tomorrowYear}-${tomorrowMonth}-${tomorrowDay}T10:00:00+07:00`
    return new Date(isoString)
  }
}
